import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from './ui/sheet';
import {
  Plus, Loader2, Trash2, Ban, FileDown, FileText, Search, Upload, Filter,
  Heart, Armchair, MapPin, ShieldOff, AlertTriangle, CheckCircle2, XCircle, X,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

const REASON_META = {
  rahat_block:       { label: 'Booked in Rahat Block',   short: 'Rahat Block',   color: 'bg-rose-100 text-rose-800 border-rose-300',     icon: Heart },
  chair_section:     { label: 'Booked in Chair Section', short: 'Chair Section', color: 'bg-blue-100 text-blue-800 border-blue-300',     icon: Armchair },
  not_in_city:       { label: 'Not in City',             short: 'Not in City',   color: 'bg-amber-100 text-amber-800 border-amber-300',  icon: MapPin },
  medical_condition: { label: 'Lazimul Faraj',           short: 'Lazimul Faraj', color: 'bg-violet-100 text-violet-800 border-violet-300', icon: ShieldOff },
};

// ============== MEMBER SEARCH (Misaq Done ladies only) ==============
const MemberSearch = ({ onPick }) => {
  const api = useApi();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/seat/members/search?q=${encodeURIComponent(q.trim())}&limit=10`);
        setResults(res.data?.members || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div className="relative">
      <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search Misaq Done lady by ITS or name…"
        className="pl-8"
        data-testid="restrict-member-search"
      />
      {q.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-72 overflow-y-auto z-50">
          {loading ? (
            <div className="p-3 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-slate-500 text-center">No Misaq Done ladies match</div>
          ) : (
            results.map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => { onPick(m); setQ(''); setResults([]); }}
                className="w-full text-left px-3 py-2 hover:bg-rose-50 border-b last:border-b-0"
                data-testid={`restrict-member-${m.its_id}`}
              >
                <div className="text-sm font-medium truncate">{m.full_name}</div>
                <div className="text-xs text-slate-500">
                  ITS {m.its_id} · age {m.age ?? '—'}
                  {m.hof_name && <> · HOF: <span className="text-slate-600">{m.hof_name.slice(0, 32)}</span></>}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ============== ADD SINGLE ==============
function AddRestrictionDialog({ open, eventId, eventLabel, onClose, onSaved }) {
  const api = useApi();
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState('medical_condition');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setPicked(null); setReason('medical_condition'); setNote(''); } }, [open]);

  const save = async () => {
    if (!picked) { toast.error('Search and pick a member first'); return; }
    if (!eventId) { toast.error('Pick a year first'); return; }
    setBusy(true);
    try {
      const res = await api.post('/seat/restrictions', {
        event_id: eventId,
        its_id: picked.its_id,
        reason,
        note,
      });
      toast.success(res.data?.updated ? 'Existing restriction updated' : `Restricted ${picked.full_name}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-restriction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-red-600" /> Add Booking Restriction</DialogTitle>
          {eventLabel && <p className="text-xs text-slate-500">For <strong>{eventLabel}</strong> only.</p>}
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Find Member *</Label>
            <p className="text-[11px] text-slate-500 mb-1">Only Misaq Done ladies are eligible for seat booking and therefore can be restricted.</p>
            {!picked ? (
              <MemberSearch onPick={setPicked} />
            ) : (
              <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded text-sm" data-testid="restrict-picked-card">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{picked.full_name}</div>
                  <div className="text-xs text-slate-600 font-mono">ITS {picked.its_id} · age {picked.age ?? '—'}</div>
                  {picked.hof_name && <div className="text-xs text-slate-500 truncate">HOF: {picked.hof_name}</div>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => setPicked(null)}><X className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold">Reason *</Label>
            <div className="grid grid-cols-1 gap-1.5 mt-1">
              {Object.entries(REASON_META).map(([code, meta]) => {
                const Icon = meta.icon;
                return (
                  <label key={code}
                    className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${reason === code ? meta.color + ' ring-2 ring-offset-1' : 'hover:bg-slate-50 border-slate-200'}`}
                    data-testid={`restrict-reason-${code}`}>
                    <input type="radio" checked={reason === code} onChange={() => setReason(code)} className="mt-1" />
                    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 text-sm font-semibold">{meta.label}</div>
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional internal note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !picked} className="bg-red-600 hover:bg-red-700" data-testid="restrict-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
            Restrict
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== BULK (with preview + select all) ==============
function BulkRestrictDialog({ open, eventId, eventLabel, onClose, onSaved }) {
  const api = useApi();
  const [step, setStep] = useState('input');   // 'input' | 'preview'
  const [reason, setReason] = useState('rahat_block');
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {rows, counts}
  const [selected, setSelected] = useState(new Set()); // its_ids selected for restriction
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setStep('input'); setReason('rahat_block'); setText(''); setNote('');
      setPreview(null); setSelected(new Set()); setResult(null); }
  }, [open]);

  const parseITS = () => {
    const nums = (text.match(/\d{6,9}/g) || []).map(s => s.trim()).filter(Boolean);
    return Array.from(new Set(nums));
  };

  const doPreview = async () => {
    const its_numbers = parseITS();
    if (its_numbers.length === 0) { toast.error('Paste at least one ITS number'); return; }
    if (!eventId) { toast.error('Pick a year first'); return; }
    setBusy(true);
    try {
      const res = await api.post('/seat/restrictions/bulk-preview', { event_id: eventId, its_numbers });
      const data = res.data || { rows: [], counts: {} };
      setPreview(data);
      // Pre-select all eligible rows
      setSelected(new Set((data.rows || []).filter(r => r.eligible).map(r => r.its_id)));
      setStep('preview');
      if (data.counts?.eligible === 0) toast.warning('No eligible rows to restrict');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const toggle = (its) => setSelected(prev => {
    const n = new Set(prev); if (n.has(its)) n.delete(its); else n.add(its); return n;
  });

  const eligibleRows = (preview?.rows || []).filter(r => r.eligible);
  const allEligibleSelected = eligibleRows.length > 0 && eligibleRows.every(r => selected.has(r.its_id));
  const toggleAll = () => {
    if (allEligibleSelected) setSelected(new Set());
    else setSelected(new Set(eligibleRows.map(r => r.its_id)));
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error('Select at least one eligible row'); return; }
    setBusy(true);
    try {
      const res = await api.post('/seat/restrictions/bulk', {
        event_id: eventId,
        its_numbers: Array.from(selected),
        reason,
        note,
        skip_ineligible: true,
      });
      setResult(res.data);
      const c = res.data?.counts || {};
      toast.success(`Restricted ${c.created || 0} · updated ${c.updated || 0} · skipped ${c.skipped || 0}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="bulk-restrict-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-red-600" /> Bulk Restrict Members{eventLabel ? ` — ${eventLabel}` : ''}</SheetTitle>
          <SheetDescription>
            Paste ITS numbers → Preview → pick eligible rows → Apply. Only Misaq Done ladies (non-HOF) can be restricted. {eventLabel ? `Applies to ${eventLabel} only.` : ''}
          </SheetDescription>
        </SheetHeader>

        {step === 'input' && (
          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-xs font-semibold">Reason *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {Object.entries(REASON_META).map(([code, meta]) => {
                  const Icon = meta.icon;
                  return (
                    <label key={code}
                      className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${reason === code ? meta.color + ' ring-2 ring-offset-1' : 'hover:bg-slate-50 border-slate-200'}`}
                      data-testid={`bulk-reason-${code}`}>
                      <input type="radio" name="bulk-reason" checked={reason === code} onChange={() => setReason(code)} className="mt-0.5" />
                      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 text-sm font-medium">{meta.label}</div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">ITS Numbers</Label>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10}
                placeholder={"40417554\n30927997\n..."}
                className="w-full border rounded p-2 text-sm font-mono mt-1" data-testid="bulk-restrict-text" />
              <div className="text-[11px] text-slate-500 mt-1">Detected: <strong>{parseITS().length}</strong> unique ITS</div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g., Rahat Block batch — 1448H" />
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap gap-2 items-center text-xs">
              <Badge className="bg-slate-700">Total: {preview.counts?.total ?? 0}</Badge>
              <Badge className="bg-emerald-600">Eligible: {preview.counts?.eligible ?? 0}</Badge>
              <Badge variant="outline" className="text-red-700 border-red-300">Ineligible: {preview.counts?.ineligible ?? 0}</Badge>
              <div className="flex-1" />
              <Badge variant="outline" className="px-2 py-1">Reason: <strong className="ml-1">{REASON_META[reason]?.label}</strong></Badge>
            </div>
            <div className="border rounded overflow-auto max-h-[55vh]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left">
                      <label className="flex items-center gap-1.5 cursor-pointer" data-testid="bulk-select-all">
                        <input
                          type="checkbox"
                          checked={allEligibleSelected}
                          onChange={toggleAll}
                          disabled={eligibleRows.length === 0}
                        />
                        <span className="text-[11px] font-semibold">All</span>
                      </label>
                    </th>
                    <th className="px-2 py-2 text-left font-semibold">ITS</th>
                    <th className="px-2 py-2 text-left font-semibold">Name</th>
                    <th className="px-2 py-2 text-left font-semibold">Misaq</th>
                    <th className="px-2 py-2 text-left font-semibold">Gender</th>
                    <th className="px-2 py-2 text-left font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(preview.rows || []).map(r => {
                    const checked = selected.has(r.its_id);
                    return (
                      <tr key={r.its_id} className={r.eligible ? '' : 'bg-rose-50/50'} data-testid={`bulk-preview-row-${r.its_id}`}>
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(r.its_id)}
                            disabled={!r.eligible}
                            data-testid={`bulk-check-${r.its_id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 font-mono text-xs">{r.its_id}</td>
                        <td className="px-2 py-1.5">{r.full_name || <span className="text-slate-400 italic">not found</span>}</td>
                        <td className="px-2 py-1.5">
                          {r.misaq_status === 'Done'
                            ? <Badge className="bg-emerald-600 text-[10px]">Done</Badge>
                            : <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">{r.misaq_status || '—'}</Badge>}
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          {r.gender || '—'}
                          {r.is_hof && <Badge variant="outline" className="ml-1 text-[10px] border-purple-300 text-purple-700">HOF</Badge>}
                        </td>
                        <td className="px-2 py-1.5">
                          {r.eligible ? (
                            <Badge className="bg-emerald-600 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-0.5" /> Eligible</Badge>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              <Badge variant="outline" className="text-red-700 border-red-300 text-[10px]"><XCircle className="w-3 h-3 mr-0.5" /> Skip</Badge>
                              <span className="text-[10px] text-red-700">{r.rejection_reason}</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {result && (
              <Card className="bg-slate-50">
                <CardContent className="p-3 text-xs space-y-1">
                  <div className="font-semibold">Bulk Result</div>
                  <div>Created: <strong className="text-emerald-700">{result.counts?.created || 0}</strong></div>
                  <div>Updated: <strong className="text-amber-700">{result.counts?.updated || 0}</strong></div>
                  <div>Skipped: <strong className="text-slate-600">{result.counts?.skipped || 0}</strong></div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <SheetFooter className="mt-6 gap-2 sticky bottom-0 bg-white pt-3 border-t">
          {step === 'input' ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={doPreview} disabled={busy || parseITS().length === 0} className="bg-red-600 hover:bg-red-700" data-testid="bulk-preview-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Preview ({parseITS().length})
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep('input')}>← Back to Edit</Button>
              <Button onClick={submit} disabled={busy || selected.size === 0} className="bg-red-600 hover:bg-red-700" data-testid="bulk-restrict-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                Restrict {selected.size} Selected
              </Button>
            </>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ============== ROOT PANEL ==============
export default function SeatRestrictionsPanel({ eventId, eventLabel }) {
  const api = useApi();
  const [stats, setStats] = useState({ counts: {} });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');     // reason code or ''
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const loadStats = useCallback(async () => {
    if (!eventId) return;
    try { const r = await api.get(`/seat/restrictions/stats?event_id=${encodeURIComponent(eventId)}`); setStats(r.data || { counts: {} }); }
    catch {}
    // eslint-disable-next-line
  }, [eventId]);
  const loadRows = useCallback(async () => {
    if (!eventId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('event_id', eventId);
      if (filter) params.set('reason', filter);
      if (q.trim()) params.set('q', q.trim());
      const r = await api.get(`/seat/restrictions?${params.toString()}`);
      setRows(r.data?.rows || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [filter, q, eventId]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const remove = async (r) => {
    if (!window.confirm(`Remove restriction for ITS ${r.its_id}? They will be able to book seats again in this event.`)) return;
    try {
      await api.delete(`/seat/restrictions/${r.id}`);
      toast.success('Restriction removed');
      loadStats(); loadRows();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const exportFile = async (fmt) => {
    if (!eventId) return;
    try {
      const params = new URLSearchParams();
      params.set('event_id', eventId);
      if (filter) params.set('reason', filter);
      params.set('format', fmt);
      const res = await api.get(`/seat/restrictions/export?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `seat_restrictions${filter ? '_' + filter : ''}.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error('Export failed'); }
  };

  if (!eventId) {
    return (
      <div className="p-6 text-center text-sm text-slate-500 bg-amber-50 border border-amber-200 rounded">
        <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-amber-600" />
        Pick a category and year to manage its booking restrictions.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Ban className="w-5 h-5 text-red-600" /> Booking Restrictions
            {eventLabel && (
              <Badge variant="outline" className="ml-1 text-xs">
                {eventLabel}
              </Badge>
            )}
          </h2>
          <p className="text-xs text-slate-500">
            Year-scoped: restrictions you add here only block bookings for <strong>{eventLabel || 'this event'}</strong>. Members may remain eligible for other years/events.
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-red-600 hover:bg-red-700" data-testid="add-restriction-btn">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
        <Button onClick={() => setShowBulk(true)} variant="outline" data-testid="bulk-restrict-btn">
          <Upload className="w-4 h-4 mr-1" /> Bulk
        </Button>
      </div>

      {/* Reason counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="restriction-stats">
        <Card className={!filter ? 'border-slate-400 ring-2 ring-slate-200' : 'cursor-pointer hover:shadow'} onClick={() => setFilter('')}>
          <CardContent className="p-3 text-center">
            <div className="text-xs text-slate-500 font-semibold uppercase">All Restricted</div>
            <div className="text-2xl font-extrabold text-slate-800">{stats.counts?.total || 0}</div>
          </CardContent>
        </Card>
        {Object.entries(REASON_META).map(([code, meta]) => {
          const Icon = meta.icon;
          const active = filter === code;
          return (
            <Card key={code}
              className={`cursor-pointer transition ${active ? meta.color.replace('bg-', 'border-').replace('-100', '-400') + ' ring-2 ring-offset-1' : 'hover:shadow'}`}
              onClick={() => setFilter(code)}
              data-testid={`stat-${code}`}>
              <CardContent className="p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-xs font-semibold uppercase">
                  <Icon className="w-3 h-3" /> {meta.short}
                </div>
                <div className="text-2xl font-extrabold">{stats.counts?.[code] || 0}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ITS"
            className="pl-8" data-testid="restrict-search" />
        </div>
        {filter && (
          <Badge variant="outline" className="px-2 py-1">
            <Filter className="w-3 h-3 mr-1" /> {REASON_META[filter]?.label}
            <button onClick={() => setFilter('')} className="ml-1 text-red-600">×</button>
          </Badge>
        )}
        <Button variant="outline" onClick={() => exportFile('excel')} data-testid="export-xl">
          <FileDown className="w-4 h-4 mr-1" /> Excel
        </Button>
        <Button variant="outline" onClick={() => exportFile('pdf')} data-testid="export-pdf">
          <FileText className="w-4 h-4 mr-1" /> PDF
        </Button>
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-red-600" /></div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-slate-400" />
              No restrictions {filter ? `for ${REASON_META[filter]?.label}` : ''}.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold">ITS</th>
                    <th className="px-2 py-2 text-left font-semibold">Name</th>
                    <th className="px-2 py-2 text-left font-semibold">HOF</th>
                    <th className="px-2 py-2 text-left font-semibold">Reason</th>
                    <th className="px-2 py-2 text-left font-semibold">Note</th>
                    <th className="px-2 py-2 text-left font-semibold">Added</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map(r => {
                    const meta = REASON_META[r.reason] || { color: '', label: r.reason_label || r.reason, icon: Ban };
                    const Icon = meta.icon;
                    return (
                      <tr key={r.id} data-testid={`restriction-row-${r.its_id}`}>
                        <td className="px-2 py-1.5 font-mono">{r.its_id}</td>
                        <td className="px-2 py-1.5">{r.member_name || <span className="text-slate-400 italic">unknown</span>}</td>
                        <td className="px-2 py-1.5 text-xs text-slate-600">{r.hof_name || '—'}</td>
                        <td className="px-2 py-1.5">
                          <Badge variant="outline" className={`text-[10px] ${meta.color}`}>
                            <Icon className="w-3 h-3 mr-0.5" /> {r.reason_label || meta.label}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-slate-600">{r.note || '—'}</td>
                        <td className="px-2 py-1.5 text-[11px] text-slate-500">
                          {(r.created_at || '').split('T')[0]}<br />
                          <span className="text-[10px]">{r.created_by_name || ''}</span>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(r)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddRestrictionDialog open={showAdd} eventId={eventId} eventLabel={eventLabel} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadStats(); loadRows(); }} />
      <BulkRestrictDialog open={showBulk} eventId={eventId} eventLabel={eventLabel} onClose={() => setShowBulk(false)} onSaved={() => { loadStats(); loadRows(); }} />
    </div>
  );
}
